/**
 * Single source of truth for role-based access on the frontend.
 *
 * Mirrors the backend `require_roles(...)` allowlists in server.py
 * (admin_get_users, admin_get_kyc_list, admin_get_trades, admin_get_deposits,
 * admin_get_withdrawals, admin_get_tickets, admin_get_audit_logs).
 *
 * `super_admin` is implicitly allowed on every admin page — the backend's
 * require_roles factory always adds SUPER_ADMIN to the allowlist, so we
 * include it explicitly here too.
 */

import {
  LayoutDashboard,
  Users,
  FileCheck,
  TrendingUp,
  ArrowDownLeft,
  ArrowUpRight,
  MessageSquare,
  FileText,
  Cpu,
  ShieldCheck,
} from 'lucide-react';

export const ROLE = {
  USER: 'user',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
  COMPLIANCE: 'compliance',
  TRADE_DESK: 'trade_desk',
  OPERATIONS: 'operations',
  SUPPORT: 'support',
  ACCOUNTANT: 'accountant',
};

export const STAFF_ROLES = [
  ROLE.ADMIN,
  ROLE.SUPER_ADMIN,
  ROLE.COMPLIANCE,
  ROLE.TRADE_DESK,
  ROLE.OPERATIONS,
  ROLE.SUPPORT,
  ROLE.ACCOUNTANT,
];

/**
 * Ordered list of admin pages. `roles` is the allowlist that can both see
 * the sidebar entry and load the route.
 */
export const ADMIN_PAGES = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    href: '/admin',
    icon: LayoutDashboard,
    roles: STAFF_ROLES, // coarse; every staff role sees the dashboard
  },
  {
    key: 'users',
    label: 'Users',
    subLabel: 'Manage users & wallets',
    href: '/admin/users',
    icon: Users,
    roles: [ROLE.ADMIN, ROLE.SUPER_ADMIN],
  },
  {
    key: 'kyc',
    label: 'KYC Approval',
    href: '/admin/kyc',
    icon: FileCheck,
    roles: [ROLE.COMPLIANCE, ROLE.SUPER_ADMIN],
  },
  {
    key: 'trade-desk',
    label: 'Trade Desk',
    href: '/admin/trade-desk',
    icon: TrendingUp,
    roles: [ROLE.TRADE_DESK, ROLE.SUPER_ADMIN],
  },
  {
    key: 'deposits',
    label: 'Deposits',
    href: '/admin/deposits',
    icon: ArrowDownLeft,
    roles: [ROLE.OPERATIONS, ROLE.SUPER_ADMIN],
  },
  {
    key: 'withdrawals',
    label: 'Withdrawals',
    href: '/admin/withdrawals',
    icon: ArrowUpRight,
    roles: [ROLE.OPERATIONS, ROLE.SUPER_ADMIN],
  },
  {
    key: 'mining',
    label: 'Mining',
    href: '/admin/mining',
    icon: Cpu,
    roles: [ROLE.OPERATIONS, ROLE.SUPER_ADMIN],
  },
  {
    key: 'tickets',
    label: 'Support Tickets',
    href: '/admin/tickets',
    icon: MessageSquare,
    roles: [ROLE.SUPPORT, ROLE.SUPER_ADMIN],
  },
  {
    key: 'audit-logs',
    label: 'Audit Logs',
    href: '/admin/audit-logs',
    icon: FileText,
    roles: [ROLE.ACCOUNTANT, ROLE.SUPER_ADMIN],
  },
  {
    key: 'admins',
    label: 'Admin Users',
    subLabel: 'Manage staff accounts',
    href: '/admin/admins',
    icon: ShieldCheck,
    roles: [ROLE.SUPER_ADMIN],
  },
];

export const isStaff = (role) => STAFF_ROLES.includes(role);

export const canAccess = (role, allowed) =>
  Array.isArray(allowed) && allowed.includes(role);

/**
 * Sidebar entries the given role should see. Always preserves the order
 * defined in ADMIN_PAGES.
 */
export const visibleAdminPages = (role) =>
  ADMIN_PAGES.filter((p) => p.roles.includes(role));

/**
 * Pick a sensible landing route for a staff user. Dashboard is allowed for
 * everyone, so it's always the answer today; kept as a function so we can
 * change the policy later (e.g. land each role on their own desk).
 */
export const adminLandingFor = (_role) => '/admin';
